import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { UploadZone } from "@/components/invoice/UploadZone";
import { InvoicePreview } from "@/components/invoice/InvoicePreview";
import { ReviewForm } from "@/components/invoice/ReviewForm";
import { AuditLogTable } from "@/components/invoice/AuditLogTable";

export default function InvoiceCapture() {
  const [stage, setStage] = useState("upload"); // upload | review | done
  const [invoice, setInvoice] = useState(null);
  const [extraction, setExtraction] = useState(null);
  const [department, setDepartment] = useState("");
  const queryClient = useQueryClient();

  const extractMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/invoices/extract", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },
    onSuccess: (data) => {
      setInvoice(data);
      setExtraction(data.extraction);
      setStage("review");
      toast.success("Invoice extracted successfully");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || "Extraction failed. Try a clearer scan.");
    },
  });

  const revalidateMutation = useMutation({
    mutationFn: async () => (await api.put(`/invoices/${invoice.id}`, { extraction })).data,
    onSuccess: (data) => {
      setInvoice((prev) => ({ ...prev, validations: data.validations }));
      toast.success("Validations refreshed");
    },
  });

  const bookMutation = useMutation({
    mutationFn: async ({ force }) =>
      (await api.post(`/invoices/${invoice.id}/book`, { extraction, department, force })).data,
    onSuccess: (data) => {
      setInvoice(data);
      setStage("done");
      queryClient.invalidateQueries({ queryKey: ["audit-log"] });
      toast.success(data.status === "booked" ? "Booked to ERP" : "Submitted with flags for review");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || "Booking failed");
    },
  });

  const reset = () => {
    setStage("upload");
    setInvoice(null);
    setExtraction(null);
    setDepartment("");
    extractMutation.reset();
  };

  const hasWarning = invoice?.validations?.some((v) => v.status === "warning");
  const hasError = invoice?.validations?.some((v) => v.status === "error");

  return (
    <main className="max-w-7xl mx-auto px-6 py-8" data-testid="invoice-capture-page">
      <div className="mb-6">
        <h1 className="text-2xl lg:text-3xl font-display font-medium text-[#141E1A]">Invoice Capture &amp; Auto-fill</h1>
        <p className="text-sm text-[#5A6B63] mt-1">
          Upload a GST invoice — Gemini extracts the fields live. Review, fix flags, then book to ERP.
        </p>
      </div>

      {stage === "upload" && (
        <UploadZone onFileSelected={(file) => extractMutation.mutate(file)} isLoading={extractMutation.isPending} />
      )}

      {(stage === "review" || stage === "done") && invoice && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-[75vh]">
            <InvoicePreview invoiceId={invoice.id} contentType={invoice.content_type} filename={invoice.filename} />
          </div>
          <div>
            <ReviewForm
              extraction={extraction}
              setExtraction={setExtraction}
              validations={invoice.validations}
              department={department}
              setDepartment={setDepartment}
              onRevalidate={() => revalidateMutation.mutate()}
              isRevalidating={revalidateMutation.isPending}
              onBook={(force) => bookMutation.mutate({ force })}
              isBooking={bookMutation.isPending}
              hasWarning={hasWarning}
              hasError={hasError}
              status={invoice.status}
              bookedBy={invoice.booked_by}
              readOnly={stage === "done"}
            />
            {stage === "done" && (
              <button
                data-testid="upload-another-invoice-button"
                onClick={reset}
                className="mt-4 text-sm font-medium text-[#0E6E52] hover:text-[#1D3730] transition-colors duration-200"
              >
                + Upload another invoice
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mt-10">
        <AuditLogTable />
      </div>
    </main>
  );
}
